import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import { findRoute } from "../../../utils/permissionUtils";
import styles from "./Code.module.sass";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { verifyOtp, verifyOtpWithMobile, reset, verifyEmailOtp, sendEmailOtp } from "../../../features/auth/authSlice";
import CustomToast from "../../../components/CustomToast";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth }from "../../../config/firebaseConfig";
const Code = ({ onConfirm, userData,setUserData, selectedTab, confirmationResult,verificationCode,setConfirmationResult,}) => {
  
  const showToast = CustomToast();
  const { isSuccess, message, isLoading,tempCodeOnSignUp } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [showError, setShowError] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const inputs = useRef([]);
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (inputs.current[0]) {
      inputs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (isSuccess && !isResending) {
      setShowError(false);
      showToast("Verification successful", "success");
      onConfirm();
    } else if (message && !isResending) {
      setShowError(true);
      setAttemptCount(prev => prev + 1);
      showToast(message.data?.err || "Invalid verification code", "error");
    }
    dispatch(reset());
  }, [isSuccess, message, isResending]);
  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^\d$/.test(value) || value === "") {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index === 5) { 
        const allDigitsFilled = newCode.every(digit => digit !== "");
        if (allDigitsFilled) {
          const completeOtp = newCode.join("");
          if (selectedTab === "phone") {
            verifyCode(completeOtp);
          } else {
            const verificationData = {
              email: userData.email,
              otp: completeOtp,
              code: tempCodeOnSignUp
            };
            dispatch(verifyEmailOtp(verificationData));
          }
        }
      } else if (value && index < 5) {
        inputs.current[index + 1].focus();
      }
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split("");
      setCode(newCode);
      newCode.forEach((digit, index) => {
        if (inputs.current[index]) {
          inputs.current[index].value = digit;
        }
      });
      if (selectedTab === "phone") {
        verifyCode(pastedData);
      } else {
        const verificationData = {
          email: userData.email,
          otp: pastedData,
          code: tempCodeOnSignUp
        };
        dispatch(verifyEmailOtp(verificationData));
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      if (code[index]) {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      } else if (index > 0) {
        inputs.current[index - 1].focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < inputs.current.length - 1) {
      inputs.current[index + 1].focus();
    }else if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const verifyCode = async (otp) => {
    try {
      if (!confirmationResult) {
        throw new Error("Confirmation result not found. Please request a new OTP.");
      }  
      const result = await confirmationResult.confirm(otp);   
      const user = result.user;
      const idToken = await user.getIdToken();  
      const payload = {
        mobile_no: userData.mobile_no,
        token: idToken,
      };
      dispatch(verifyOtpWithMobile(payload));
      showToast("Phone number verified successfully!", "success");
    } catch (error) {
      console.error("OTP Verification Error:", error.message);
      if (error.code === "auth/invalid-verification-code") {
        showToast("Invalid OTP. Please try again.", "error");
      } else if (error.code === "auth/code-expired") {
        showToast("The verification code has expired. Please request a new one.", "error");
      } else {
        showToast("Verification failed. Please try again.", "error");
      }
    }
  };
 
  const handleSubmit = async () => {
    const otp = code.join("");
    setShowError(false);
    setIsResending(false);
    if (selectedTab === "phone") {
      if (code.some((num) => num === "")) return;
      await verifyCode(otp);
    } else {
      if (code.some((num) => num === "")) return;
      try {
        const verificationData = {
          email: userData.email,
          otp: otp,
          code: tempCodeOnSignUp
        };
        await dispatch(verifyEmailOtp(verificationData));
      } catch (error) {
        setShowError(true);
        showToast("Verification failed", "error");
      }
    }
  };

  const isButtonDisabled = code.some((num) => num === "");

  const setupRecaptchaVerifier = () => {
    const recaptchaContainer = document.getElementById("recaptcha-container");
    if (recaptchaContainer && !window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: (response) => {
          window.recaptchaVerifier.recaptcha.reset();
        },
      });
    }
  };

  const sendVerificationCode = async (phoneNumber) => {
    try {
      setupRecaptchaVerifier();
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
    } catch (error) {
      console.error("Phone auth error:", error);
      showToast("Failed to send verification code", "error");
    }
  };
  const handleResend = async () => {
    setIsResending(true);
    try {
      if (selectedTab === "email") {
        // Resend email OTP
        const response = await dispatch(sendEmailOtp({ email: userData.email }));
        if (response.payload?.message?.code) {
          setUserData({
            ...userData,
            tempCodeOnSignUp: response.payload.message.code
          });
          showToast("Verification code resent to your email", "success");
        } else {
          throw new Error("Failed to send email verification code");
        }
      } else if (selectedTab === "phone") {
        // Resend phone OTP
        const phoneNumber = userData.mobile_no; 
        await sendVerificationCode(phoneNumber);
        showToast("OTP has been resent", "success");
      }
  
      setCode(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
    } catch (error) {
      console.error("Resend error:", error);
      showToast(`Failed to resend verification code: ${error.message}`, "error");
    } finally {
      setIsResending(false);
    }
  };
  
  return (
    <div className={styles.code}>
      <div className={styles.body}>
        <div className={cn(styles.note, "mb-[20px]")}>
          {selectedTab === "phone" ? (
            "Please check your phone, we've sent a verification code to " +
            userData.mobile_no
          ) : (
            <>
              Please check your email, we've sent a verification code to{" "}
              <span className="text-black">{userData.email}</span>
            </>
          )}
        </div>
        <div className={styles.fieldset}>
          {code.map((num, index) => (
            <div className={styles.field} key={`field-${index}`}>
              <input
                className={styles.input}
                name={`number${index + 1}`}
                type="tel"
                autoComplete="off"
                required
                value={num}
                onChange={(e) => handleChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={handlePaste}
                ref={(el) => (inputs.current[index] = el)}
              />
            </div>
          ))}
        </div>
        <div className={styles.errorNote}>
          The code you entered is incorrect.
        </div>
        <button
          className={cn("button", styles.button)}
          onClick={handleSubmit}
          disabled={isButtonDisabled || isLoading}
        >
          {isLoading ? (
            <Loader className={styles.loader} white />
          ) : (
            <span>Continue</span>
          )}
        </button>
        <div className={cn(styles.note, "mt-4")}>
          {selectedTab === "phone"
            ? "Didn't get the SMS? Make sure your phone number is correct or "
            : "Didn't get the email? Check your spam folder or "}{" "}
          <button
            className="text-blue-500 hover:underline"
            onClick={handleResend}
          >
            resend
          </button>{" "}
          the code.
        </div>
        <div className={styles.note}>
          This site is protected by reCAPTCHA. By logging in, you agree to the{" "}
          <a href="/" className="text-blue-500 hover:underline">
            Terms of Service
          </a>{" "}
          &{" "}
          <a href="/privacy" className="text-blue-500 hover:underline">
            Privacy Policy
          </a>
          .
        </div>
      </div>
    </div>
  );
};

export default Code;
